import { useEffect, useState } from 'react'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

// Client-side defaults — must stay in sync with server ROLE_DEFAULTS in routes/roleNav.js
const ROLE_DEFAULTS: Record<string, string[]> = {
  // Tenant staff
  read_only:             ['dashboard', 'incidents', 'lone-worker', 'competencies'],
  field_worker:          ['dashboard', 'incidents', 'lone-worker', 'permits', 'audits', 'competencies'],
  supervisor:            ['dashboard', 'incidents', 'incidents-register', 'lone-worker', 'permits', 'audits', 'risk-register', 'corrective-actions', 'competencies'],
  ops_safety:            ['dashboard', 'incidents', 'incidents-register', 'investigations', 'lone-worker', 'permits', 'audits', 'risk-register', 'assets', 'competencies', 'workforce', 'contractors', 'workers-comp', 'corrective-actions', 'metrics', 'analytics', 'scheduled-reports'],
  corp_safety:           ['dashboard', 'incidents', 'incidents-register', 'investigations', 'psychosocial', 'lone-worker', 'permits', 'audits', 'risk-register', 'assets', 'competencies', 'workforce', 'contractors', 'workers-comp', 'corrective-actions', 'metrics', 'analytics', 'scheduled-reports'],
  tenant_admin:          ['dashboard', 'incidents', 'incidents-register', 'investigations', 'psychosocial', 'lone-worker', 'permits', 'audits', 'risk-register', 'assets', 'competencies', 'workforce', 'contractors', 'workers-comp', 'corrective-actions', 'metrics', 'analytics', 'scheduled-reports'],
  // Contractor company hierarchy (all scoped to their contractor_id)
  contractor_worker:     ['dashboard', 'incidents', 'lone-worker', 'permits', 'audits', 'competencies'],
  contractor_standard:   ['dashboard', 'incidents', 'lone-worker', 'permits', 'audits', 'competencies'],
  contractor_supervisor: ['dashboard', 'incidents', 'incidents-register', 'lone-worker', 'permits', 'audits', 'risk-register', 'corrective-actions', 'competencies'],
  contractor_safety:     ['dashboard', 'incidents', 'incidents-register', 'investigations', 'lone-worker', 'permits', 'audits', 'risk-register', 'corrective-actions', 'competencies', 'workforce'],
  contractor_admin:      ['dashboard', 'incidents', 'incidents-register', 'investigations', 'lone-worker', 'permits', 'audits', 'risk-register', 'assets', 'corrective-actions', 'competencies', 'workforce', 'metrics'],
}

let cache: { role: string; allowed: string[] } | null = null

export function useNavPermissions() {
  const { user, isAuthenticated } = useAuthStore()
  const role = user?.role || 'field_worker'

  const [allowed, setAllowed] = useState<string[]>(() => {
    if (cache && cache.role === role) return cache.allowed
    return ROLE_DEFAULTS[role] || ROLE_DEFAULTS.field_worker
  })

  useEffect(() => {
    if (!isAuthenticated) return
    if (cache?.role === role) { setAllowed(cache.allowed); return }

    api.get('/role-nav').then(({ data }) => {
      cache = { role: data.role, allowed: data.allowed }
      setAllowed(data.allowed)
    }).catch(() => {
      // fall back to client-side defaults silently
    })
  }, [isAuthenticated, role])

  return { allowed, can: (key: string) => allowed.includes(key) }
}

// Call this to bust the cache when tenant admin saves new config
export function invalidateNavCache() {
  cache = null
}
